import React, { useState, useEffect } from 'react';
import '@/App.css';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  Phone, MessageSquare, Bot, ClipboardList, Building2, Brain, 
  FileText, TrendingUp, Fingerprint, MessagesSquare,
  Menu, X, Globe, ArrowRight, Check, Star, Users, Zap,
  Shield, BarChart3, Mail, Clock, MapPin
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping
const iconMap = {
  Phone, MessageSquare, Bot, ClipboardList, Building2, Brain,
  FileText, TrendingUp, Fingerprint, MessagesSquare
};

const App = () => {
  const { t, i18n } = useTranslation();
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [priceCalc, setPriceCalc] = useState({
    service: '',
    agents: '',
    lines: '',
    monthly_calls: '',
    monthly_sms: ''
  });
  const [priceResult, setPriceResult] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredServices(services);
    } else {
      const filtered = Array.isArray(services) 
        ? services.filter(s => s.category === selectedCategory)
        : [];
      setFilteredServices(filtered);
    }
  }, [selectedCategory, services]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      const servicesData = Array.isArray(response.data) ? response.data : [];
      setServices(servicesData);
      setFilteredServices(servicesData);
    } catch (error) {
      console.error('Error fetching services:', error);
      // Set empty array on error to prevent crash
      setServices([]);
      setFilteredServices([]);
    }
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'es' ? 'en' : 'es';
    i18n.changeLanguage(newLang);
  };

  const calculatePrice = async () => {
    try {
      const payload = {
        service: priceCalc.service,
        ...(priceCalc.agents && { agents: parseInt(priceCalc.agents) }),
        ...(priceCalc.lines && { lines: parseInt(priceCalc.lines) }),
        ...(priceCalc.monthly_calls && { monthly_calls: parseInt(priceCalc.monthly_calls) }),
        ...(priceCalc.monthly_sms && { monthly_sms: parseInt(priceCalc.monthly_sms) })
      };
      const response = await axios.post(`${API}/calculate-price`, payload);
      setPriceResult(response.data);
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contact`, {
        ...contactForm,
        language: i18n.language
      });
      setFormStatus({ type: 'success', message: t('contact.form.success') });
      setContactForm({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: ''
      });
      setTimeout(() => setFormStatus({ type: '', message: '' }), 5000);
    } catch (error) {
      console.error('Error submitting contact:', error);
      setFormStatus({ type: 'error', message: t('contact.form.error') });
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-900 relative overflow-hidden">
      {/* VIDEO DE FONDO */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <video
          id="bg-video"
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/bg-video.webm" type="video/webm" />
          <source src="/background-video.mp4" type="video/mp4" />
        </video>
        {/* Overlay sutil para legibilidad */}
        <div className="absolute inset-0 bg-slate-950/60"></div>
      </div>
      
      {/* Content with higher z-index */}
      <div className="relative z-10">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-slate-950/80 backdrop-blur-md border-b border-white/10" role="navigation" aria-label="Navegación principal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img 
                src="/logo.png" 
                alt="Call Market - Soluciones de Contact Center con IA" 
                className="h-10 w-auto"
              />
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('services')} className="text-white/90 hover:text-white transition">
                {t('nav.services')}
              </button>
              <button onClick={() => scrollToSection('pricing')} className="text-white/90 hover:text-white transition">
                {t('nav.pricing')}
              </button>
              <button onClick={() => scrollToSection('features')} className="text-white/90 hover:text-white transition">
                {t('nav.about')}
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-white/90 hover:text-white transition">
                {t('nav.contact')}
              </button>
              <button 
                onClick={toggleLanguage}
                className="flex items-center space-x-1 text-white/90 hover:text-white transition"
              >
                <Globe className="w-4 h-4" />
                <span>{i18n.language.toUpperCase()}</span>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-white"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-4">
              <button onClick={() => scrollToSection('services')} className="block text-white/90 hover:text-white transition">
                {t('nav.services')}
              </button>
              <button onClick={() => scrollToSection('pricing')} className="block text-white/90 hover:text-white transition">
                {t('nav.pricing')}
              </button>
              <button onClick={() => scrollToSection('features')} className="block text-white/90 hover:text-white transition">
                {t('nav.about')}
              </button>
              <button onClick={() => scrollToSection('contact')} className="block text-white/90 hover:text-white transition">
                {t('nav.contact')}
              </button>
              <button onClick={toggleLanguage} className="flex items-center space-x-1 text-white/90 hover:text-white transition">
                <Globe className="w-4 h-4" />
                <span>{i18n.language.toUpperCase()}</span>
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden" role="banner" aria-labelledby="hero-title">
        {/* Semi-transparent gradient over video */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/70 via-blue-950/60 to-indigo-950/70" aria-hidden="true" />
        
        {/* Animated Gradient Overlay */}
        <div className="absolute inset-0 gradient-animate bg-gradient-to-br from-teal-900/30 via-cyan-900/30 to-emerald-900/30 opacity-50" aria-hidden="true" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-white space-y-6">
              <h1 id="hero-title" className="text-5xl md:text-7xl font-bold leading-tight">
                {t('hero.title')}
                <br />
                <span className="gradient-text">{t('hero.subtitle')}</span>
              </h1>
              <p className="text-xl text-white/80">
                {t('hero.description')}
              </p>
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full font-semibold hover:from-emerald-600 hover:to-teal-600 transition-all transform hover:scale-105 flex items-center space-x-2"
                  data-testid="hero-cta-button"
                >
                  <span>{t('hero.cta')}</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => scrollToSection('services')}
                  className="px-8 py-4 glass rounded-full font-semibold hover:bg-white/20 transition-all"
                  data-testid="hero-services-button"
                >
                  {t('hero.ctaSecondary')}
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 pt-8">
                <div className="text-center">
                  <div className="text-3xl font-bold gradient-text">50K+</div>
                  <div className="text-sm text-white/60">{t('hero.stats.calls')}</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold gradient-text">98%</div>
                  <div className="text-sm text-white/60">{t('hero.stats.satisfaction')}</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold gradient-text">+45%</div>
                  <div className="text-sm text-white/60">{t('hero.stats.efficiency')}</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl animate-float">
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-auto"
                  aria-label="Video demonstrativo de Call Market - Centro de contacto profesional"
                >
                  <source src="/hero-video.webm" type="video/webm" />
                  <source src="/hero-video.mp4" type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="relative py-20 px-4 sm:px-6 lg:px-8 bg-slate-950/60" role="region" aria-labelledby="services-title">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 id="services-title" className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t('services.title')}
            </h2>
            <p className="text-xl text-white/70">{t('services.subtitle')}</p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {['all', 'core', 'ai', 'analytics', 'security', 'specialized'].map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  selectedCategory === cat
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white'
                    : 'glass text-white/70 hover:text-white'
                }`}
                data-testid={`category-${cat}-button`}
              >
                {t(`services.categories.${cat}`)}
              </button>
            ))}
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredServices && Array.isArray(filteredServices) && filteredServices.length > 0 ? (
              filteredServices.map(service => {
              const Icon = iconMap[service.icon] || Phone;
              const name = i18n.language === 'es' ? service.name_es : service.name_en;
              const description = i18n.language === 'es' ? service.description_es : service.description_en;
              const features = i18n.language === 'es' ? service.features_es : service.features_en;

              return (
                <div 
                  key={service.id}
                  className="glass rounded-2xl p-6 hover:bg-white/20 transition-all transform hover:scale-105 cursor-pointer"
                  data-testid={`service-card-${service.id}`}
                >
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2">{name}</h3>
                      <p className="text-white/70 text-sm">{description}</p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {features.slice(0, 4).map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-white/60 text-sm">
                        <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button 
                    onClick={() => scrollToSection('contact')}
                    className="mt-6 w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg font-medium text-white hover:from-emerald-600 hover:to-teal-600 transition-all"
                  >
                    {t('services.cta')}
                  </button>
                </div>
              );
            })
            ) : (
              <div className="col-span-3 text-center py-12">
                <p className="text-white/70 text-lg">{t('services.loading') || 'Cargando servicios...'}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative py-20 px-4 sm:px-6 lg:px-8 bg-blue-950/60">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t('features.title')}
            </h2>
            <p className="text-xl text-white/70">{t('features.subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center space-y-4" data-testid="feature-ai">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white">{t('features.items.ai.title')}</h3>
              <p className="text-white/70">{t('features.items.ai.description')}</p>
            </div>

            <div className="text-center space-y-4" data-testid="feature-support">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-cyan-500 to-teal-600 rounded-2xl flex items-center justify-center">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white">{t('features.items.support.title')}</h3>
              <p className="text-white/70">{t('features.items.support.description')}</p>
            </div>

            <div className="text-center space-y-4" data-testid="feature-integration">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white">{t('features.items.integration.title')}</h3>
              <p className="text-white/70">{t('features.items.integration.description')}</p>
            </div>

            <div className="text-center space-y-4" data-testid="feature-security">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-emerald-600 to-cyan-600 rounded-2xl flex items-center justify-center">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white">{t('features.items.security.title')}</h3>
              <p className="text-white/70">{t('features.items.security.description')}</p>
            </div>

            <div className="text-center space-y-4" data-testid="feature-scalable">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-teal-600 to-emerald-500 rounded-2xl flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white">{t('features.items.scalable.title')}</h3>
              <p className="text-white/70">{t('features.items.scalable.description')}</p>
            </div>

            <div className="text-center space-y-4" data-testid="feature-analytics">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-cyan-600 to-teal-500 rounded-2xl flex items-center justify-center">
                <BarChart3 className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white">{t('features.items.analytics.title')}</h3>
              <p className="text-white/70">{t('features.items.analytics.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Calculator */}
      <section id="pricing" className="relative py-20 px-4 sm:px-6 lg:px-8 bg-slate-950/60">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t('pricing.title')}
            </h2>
            <p className="text-xl text-white/70">{t('pricing.subtitle')}</p>
          </div>

          <div className="glass rounded-2xl p-8 space-y-6">
            <div>
              <label className="block text-white mb-2 font-medium">{t('pricing.selectService')}</label>
              <select
                value={priceCalc.service}
                onChange={(e) => setPriceCalc({...priceCalc, service: e.target.value})}
                className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg border border-white/10 focus:border-emerald-500 outline-none"
                data-testid="pricing-service-select"
              >
                <option value="">{t('pricing.selectService')}</option>
                {Array.isArray(services) && services.map(s => (
                  <option key={s.id} value={s.id}>
                    {i18n.language === 'es' ? s.name_es : s.name_en}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white mb-2 font-medium">{t('pricing.agents')}</label>
                <input
                  type="number"
                  value={priceCalc.agents}
                  onChange={(e) => setPriceCalc({...priceCalc, agents: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg border border-white/10 focus:border-emerald-500 outline-none"
                  placeholder="10"
                  data-testid="pricing-agents-input"
                />
              </div>

              <div>
                <label className="block text-white mb-2 font-medium">{t('pricing.lines')}</label>
                <input
                  type="number"
                  value={priceCalc.lines}
                  onChange={(e) => setPriceCalc({...priceCalc, lines: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg border border-white/10 focus:border-emerald-500 outline-none"
                  placeholder="5"
                  data-testid="pricing-lines-input"
                />
              </div>

              <div>
                <label className="block text-white mb-2 font-medium">{t('pricing.monthlyCalls')}</label>
                <input
                  type="number"
                  value={priceCalc.monthly_calls}
                  onChange={(e) => setPriceCalc({...priceCalc, monthly_calls: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg border border-white/10 focus:border-emerald-500 outline-none"
                  placeholder="10000"
                  data-testid="pricing-calls-input"
                />
              </div>

              <div>
                <label className="block text-white mb-2 font-medium">{t('pricing.monthlySms')}</label>
                <input
                  type="number"
                  value={priceCalc.monthly_sms}
                  onChange={(e) => setPriceCalc({...priceCalc, monthly_sms: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg border border-white/10 focus:border-emerald-500 outline-none"
                  placeholder="5000"
                  data-testid="pricing-sms-input"
                />
              </div>
            </div>

            <button
              onClick={calculatePrice}
              disabled={!priceCalc.service}
              className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg font-semibold text-white hover:from-emerald-600 hover:to-teal-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="calculate-price-button"
            >
              {t('pricing.calculate')}
            </button>

            {priceResult && (
              <div className="mt-8 p-6 bg-gradient-to-br from-emerald-900/50 to-teal-900/50 rounded-xl border border-emerald-500/30" data-testid="price-result">
                <div className="grid md:grid-cols-2 gap-6 mb-4">
                  <div>
                    <div className="text-white/70 text-sm mb-1">{t('pricing.result.setup')}</div>
                    <div className="text-3xl font-bold text-white">
                      ${priceResult.setup_cost.toLocaleString()} {priceResult.currency}
                    </div>
                  </div>
                  <div>
                    <div className="text-white/70 text-sm mb-1">{t('pricing.result.monthly')}</div>
                    <div className="text-3xl font-bold text-white">
                      ${priceResult.monthly_cost.toLocaleString()} {priceResult.currency}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative py-20 px-4 sm:px-6 lg:px-8 bg-blue-950/60">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t('contact.title')}
            </h2>
            <p className="text-xl text-white/70">{t('contact.subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Form */}
            <div className="glass rounded-2xl p-8">
              <form onSubmit={submitContact} className="space-y-4" data-testid="contact-form">
                <div>
                  <input
                    type="text"
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    placeholder={t('contact.form.name')}
                    className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg border border-white/10 focus:border-emerald-500 outline-none"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <input
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    placeholder={t('contact.form.email')}
                    className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg border border-white/10 focus:border-emerald-500 outline-none"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <input
                    type="tel"
                    required
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                    placeholder={t('contact.form.phone')}
                    className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg border border-white/10 focus:border-emerald-500 outline-none"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div>
                  <input
                    type="text"
                    value={contactForm.company}
                    onChange={(e) => setContactForm({...contactForm, company: e.target.value})}
                    placeholder={t('contact.form.company')}
                    className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg border border-white/10 focus:border-emerald-500 outline-none"
                    data-testid="contact-company-input"
                  />
                </div>

                <div>
                  <select
                    required
                    value={contactForm.service}
                    onChange={(e) => setContactForm({...contactForm, service: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg border border-white/10 focus:border-emerald-500 outline-none"
                    data-testid="contact-service-select"
                  >
                    <option value="">{t('contact.form.service')}</option>
                    {Array.isArray(services) && services.map(s => (
                      <option key={s.id} value={s.id}>
                        {i18n.language === 'es' ? s.name_es : s.name_en}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <textarea
                    required
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    placeholder={t('contact.form.message')}
                    rows={4}
                    className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg border border-white/10 focus:border-purple-500 outline-none resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg font-semibold text-white hover:from-emerald-600 hover:to-teal-600 transition-all"
                  data-testid="contact-submit-button"
                >
                  {t('contact.form.submit')}
                </button>

                {formStatus.message && (
                  <div className={`p-4 rounded-lg ${
                    formStatus.type === 'success' 
                      ? 'bg-green-500/20 border border-green-500/50 text-green-400'
                      : 'bg-red-500/20 border border-red-500/50 text-red-400'
                  }`} data-testid="contact-form-status">
                    {formStatus.message}
                  </div>
                )}
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="glass rounded-2xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-white font-semibold mb-1">{t('contact.info.email')}</div>
                    <a href="mailto:contacto@callmarket.cc" className="text-emerald-400 hover:text-emerald-300 transition">
                      contacto@callmarket.cc
                    </a>
                  </div>
                </div>
              </div>

              <div className="glass rounded-2xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-xl">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-white font-semibold mb-1">{t('contact.info.hours')}</div>
                    <div className="text-white/70">{t('contact.info.hoursValue')}</div>
                  </div>
                </div>
              </div>

              <div className="glass rounded-2xl p-6">
                <img 
                  src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxjYWxsJTIwY2VudGVyfGVufDB8fHx8MTc3MjI5NjE3NHww&ixlib=rb-4.1.0&q=85"
                  alt="Oficina moderna de Call Market - Equipo profesional de contact center"
                  className="rounded-xl w-full"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-slate-950/80 py-12 px-4 sm:px-6 lg:px-8 border-t border-white/10" role="contentinfo">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="/logo.png" 
                  alt="Call Market - Líder en soluciones de contact center" 
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-white/60 mb-4">{t('footer.description')}</p>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">{t('footer.quickLinks')}</h3>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-white/60 hover:text-white transition">
                    {t('nav.services')}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('pricing')} className="text-white/60 hover:text-white transition">
                    {t('nav.pricing')}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contact')} className="text-white/60 hover:text-white transition">
                    {t('nav.contact')}
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">{t('footer.legal')}</h3>
              <ul className="space-y-2">
                <li><a href="/legal/privacidad.html" className="text-white/60 hover:text-white transition">{t('footer.privacy')}</a></li>
                <li><a href="/legal/terminos.html" className="text-white/60 hover:text-white transition">{t('footer.terms')}</a></li>
                <li><a href="/legal/eliminar-datos.html" className="text-white/60 hover:text-white transition">{t('footer.dataDeletion')}</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 text-center text-white/60">
            <p>© 2025 Call Market. {t('footer.rights')}</p>
            <p className="mt-2 text-sm text-white/40">{t('footer.entity')}</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/5219994591777"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-16 h-16 bg-green-500 rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 transition-all hover:scale-110 z-50"
        data-testid="whatsapp-button"
      >
        <MessageSquare className="w-8 h-8 text-white" />
      </a>
      </div> {/* Close content wrapper */}
    </div>
  );
};

export default App;
